import {
  reactive,
  ref,
  Ref,
  watch,
  computed,
  getCurrentInstance,
  onMounted,
  ComponentInternalInstance,
  onUnmounted,
  UnwrapRef,
} from '@vue/composition-api';
import { DialogPlugin, DialogInstance } from 'tdesign-vue';
import { debounce } from 'lodash';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入 compositions

// 引入工具类
import smh, { SMH } from '@/renderer/scripts/smh';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import store from '@/renderer/store';
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';
import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';
import { Sort } from '@/renderer/components/SpacePage/compositions/useSort';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type ListItemInfo = SMH.RecycledListItemInfo & {
  virusAuditStatus?: number;
  sensitiveWordAuditStatus?: number;
};
type TableData = {
  list: ListItemInfo[];
  nextMarker: number;
  selectedRowKeys: Array<string | number>;
  selectedList: ListItemInfo[];
  canRestoreFiles: SMH.RecycledItem[];
  canDeleteFiles: SMH.RecycledItem[];
};

type Handler = {
  tableData: UnwrapRef<TableData>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  methods: {
    pullList: (type: string, scope: Drawer.FilterData) => void;
    refresh: () => void;
    silentRefresh: () => void;
    onScroll: (params: { e: WheelEvent }) => void;
    onDelete: (items: SMH.RecycledItem[], num: number) => void;
    getMoreData: () => void;
    onRestore: (items: SMH.RecycledItem[]) => void;
  };
  loadingStatus: Ref<string>;
  columns: Array<any>;
  sort: Sort;
  totalNum: Ref<number>;
};

/**
 * 还原失败的文件展示的tips
 */
const restoreFailState = new Map([
  ['RecycledItemNotFound', i18n_t('文件找不到，可能已被管理员删除')],
  ['FileRemovedByQuota', i18n_t('文件所在空间已满，已被自动删除')],
  ['DirectoryNotFound', i18n_t('父文件路径不存在')],
  ['SourceFileNotFound', i18n_t('还原路径找不到')],
  ['SameNameDirectoryOrFileExists', i18n_t('文件所在目录存在同名文件，暂时无法恢复')],
]);
export default function initList(spaceId?: Ref<SMH.SpaceId>): Handler {
  // 当前组件
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const tableData = reactive<TableData>({
    list: [],
    nextMarker: -1,
    selectedRowKeys: [],
    selectedList: [],
    canRestoreFiles: [],
    canDeleteFiles: [],
  });
  const { methods: extraFileInfoMethods } = extraFileInfoHandler(spaceId);
  const totalNum = ref(0);
  const columns: Array<any> = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 26,
    },
    {
      colKey: 'removalTime',
      cell: 'removalTime',
      title: i18n_t('删除时间'),
      width: 160,
    },
    {
      colKey: 'name',
      cell: 'name',
      title: i18n_t('文件名'),
      width: 200,
    },
    {
      colKey: 'removed',
      cell: 'removed',
      title: i18n_t('删除人'),
      width: 100,
      ellipsis: {
        disabled: true,
      },
    },
    {
      colKey: 'filePath',
      cell: 'filePath',
      title: i18n_t('文件路径'),
      width: 170,
      ellipsis: true,
    },
    {
      colKey: 'size',
      title: i18n_t('文件大小'),
      width: 80,
    },
  ];
  const loading = ref(true);
  const loadingOnHeader = ref(false);
  const loadingStatus = ref('');
  const sort = reactive<Sort>({
    sortBy: 'removalTime',
    descending: true,
  });

  // 记录文件筛选的数据
  const fileScope = ref<Drawer.FilterData>({
    teamScope: [],
    collaborationScope: [],
    personalScope: [],
    selectView: 'mine', // 选中的查看范围
  });

  const userId = computed(() => store.getters.userId);
  let preTableData: any = null;

  const methods = {
    // 过滤出有操作权限的文件
    filterCanOptions(data: any) {
      tableData.canRestoreFiles = [];
      tableData.canDeleteFiles = [];
      // 过滤出可还原或者删除的文件
      data.forEach((item: any) => {
        if (item.authorityList.canRestore && !item.showFailTips) {
          tableData.canRestoreFiles.push(item);
        }
        if (item.authorityList.canDelete) {
          tableData.canDeleteFiles.push(item);
        }
      });
    },
    modifyScopeData(scope: Drawer.FilterData) {
      const spaceItems: SMH.ListRecycledParams['spaceItems'] = [];
      spaceItems.push(...scope.teamScope, ...scope.collaborationScope, ...scope.personalScope);
      return spaceItems;
    },
    async pullList(type: string, scope: Drawer.FilterData, silentLoading = false) {
      const startTime = new Date().getTime();
      if (scope) {
        fileScope.value = scope;
      }
      if (type !== 'scroll') {
        loading.value = !silentLoading;
        loadingOnHeader.value = silentLoading;
      }
      const opt = {
        limit: 20,
        orderBy: sort.sortBy,
        orderByType: sort.descending ? 'desc' : 'asc',
        spaceId: spaceId?.value,
        removedBy: fileScope.value.selectView === 'mine' ? userId.value : '',
      };
      // 只有当scroll的时候需要增加这个字段并且tableData.nextMarker 不为负数的时候，增加mark字段
      if (type === 'scroll' && tableData.nextMarker > 0) {
        Object.assign(opt, {
          marker: tableData.nextMarker,
        });
      } else {
        Object.assign(opt, {});
      }
      try {
        const spaceItems = methods.modifyScopeData(scope);
        const { data: listRes } = await smh.userAPI.listRecycled({
          ...opt,
          spaceItems,
        });
        tableData.nextMarker = listRes?.nextMarker;
        // 对文件路径和文件初始状态进行处理
        listRes?.contents.map((item: any) => {
          // 处理文件路径
          if (item?.user) {
            item.filePath = i18n_t('个人空间/{slot1}', {
              slot1: item.originalPath.join('/'),
            });
          } else if (item?.team) {
            item.filePath = i18n_t('企业空间/{slot1}/{slot2}', {
              slot1: item.team.name,
              slot2: item.originalPath.join('/'),
            });
          } else if (item?.group) {
            item.filePath = i18n_t('协作空间/{slot1}/{slot2}', {
              slot1: item.group.name,
              slot2: item.originalPath.join('/'),
            });
          }
          // 处理初始文件状态
          const { canRestore, canDelete } = item.authorityList;
          if (!canRestore || !canDelete) {
            if (!canRestore && canDelete) {
              item.stateName = i18n_t('无恢复权限');
            } else if (canRestore && !canDelete) {
              item.stateName = i18n_t('无删除权限');
            } else {
              item.stateName = i18n_t('无操作权限');
            }
            item.showDefaultTips = true;
          }
          if (item.isOverRestoreTime) {
            item.stateName = i18n_t('文件超过还原期限');
            item.showDefaultTips = true;
          }
          if (preTableData) {
            preTableData.forEach((cur: any) => {
              if (item.recycledItemId === cur.recycledItemId) {
                item.showFailTips = true;
                item.stateName = cur.stateName;
              }
            });
          }
          return item;
        });
        // 只有当滚动的时候需要push
        if (type === 'scroll') {
          tableData.list.push(...listRes.contents);
        } else {
          tableData.list = listRes.contents;
        }
        tableData.list = tableData.list.map((item: any, index: number) => ({
          ...item,
          id: index,
          path: item.originalPath,
        }));
        extraFileInfoMethods.getFileExtraRefresh(tableData, { targetList: listRes.contents });
        totalNum.value = listRes.totalNum;
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.RECYCLE_TOTAL, totalNum.value);
        reporter.report('list_cost_time', {
          page: 'RecycleBin',
          cost: new Date().getTime() - startTime,
          count: 20,
        });
      } catch (error: MsgError) {
        loading.value = false;
        loadingOnHeader.value = false;
        tips.error(error);
      }
    },
    refresh() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.pullList('pull', fileScope.value);
    },
    silentRefresh() {
      if (!loading.value && !loadingOnHeader.value) {
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        methods.pullList('pull', fileScope.value, true);
      }
    },
    async getMoreData() {
      loadingStatus.value = 'loading';
      await methods.pullList('scroll', fileScope.value);
      loadingStatus.value = '';
    },
    async onScroll(params: { e: WheelEvent }) {
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      if (clientHeight + scrollTop >= scrollHeight - 100) {
        // 当文件总数和现在已经展示的数据一样多，不加载
        if (totalNum.value === tableData.list.length) return;
        loadingStatus.value = 'loading';
        methods.scrollGetData();
      }
    },
    scrollGetData: debounce(async () => {
      await methods.pullList('scroll', fileScope.value);
      loadingStatus.value = '';
    }, 500),
    async modifyData(list: SMH.RecycledItem[]) {
      const recycleItems: SMH.RecycledItem[] = [];
      // 是通过item的spaceId的，不需要获取个人空间的spaceId
      recycleItems.push(
        ...list.map(item => ({
          spaceId: item.spaceId,
          recycledItemId: item.recycledItemId,
        })),
      );
      return recycleItems;
    },
    // 删除的操作
    onDelete(items: SMH.RecycledItem[], num: number) {
      let headerTips = i18n_t('确定要永久删除所选内容吗？');
      let bodyTips = i18n_t('删除后无法恢复文件');
      if (items.length > 1) {
        headerTips = i18n_t('{slot1}个文件将被彻底删除', {
          slot1: items.length,
        });
        if (items.length - num === 0) {
          bodyTips = i18n_t('选择{slot1}个文件，{num}个文件可删除', {
            slot1: items.length,
            num,
          });
        } else {
          bodyTips = i18n_t('选择{slot1}个文件，{num}个文件可删除，其中{slot2}个文件无删除权限', {
            slot1: items.length,
            num,
            slot2: items.length - num,
          });
        }
      }
      const deleteDialog: DialogInstance = CustomDialog.confirm({
        header: headerTips,
        body: bodyTips,
        theme: 'warning',
        onConfirm: () => {
          if (deleteDialog.hide) {
            deleteDialog.hide();
          }
          methods.handleDelete(tableData.canDeleteFiles);
        },
      });
    },
    async handleDelete(items: SMH.RecycledItem[]) {
      loading.value = true;
      try {
        tips.loading(i18n_t('正在删除文件'));
        const recycledItems = await methods.modifyData(items);
        await smh.userAPI.deleteRecycled({
          recycledItems,
        });
        tips.success(i18n_t('文件删除成功'), 1000);
        methods.refresh();
      } catch (error: MsgError) {
        tips.error(error);
        loading.value = false;
      }
    },
    // 还原的操作
    async onRestore(items: SMH.RecycledItem[]) {
      loading.value = true;
      try {
        // tips.loading('正在还原文件');
        const recycledItems = await methods.modifyData(items);
        const res = await smh.userAPI.restoreRecycled({
          recycledItems,
        });
        preTableData = [];
        if (res.status === 207 || res.status === 200) {
          res.data.result.forEach((item: any) => {
            // 有code的时候说明这个文件是还原失败的
            if (item?.code) {
              preTableData.push(
                ...tableData.list
                  .filter(cur => item.recycledItemId === cur.recycledItemId)
                  .map(cur => {
                    return {
                      recycledItemId: cur.recycledItemId,
                      showFailTips: true,
                      stateName: restoreFailState.get(item.code) || '',
                    };
                  }),
              );
            }
          });
        }
        tips.closeAll();
        methods.refresh();
      } catch (error: MsgError) {
        tips.error(error);
        loading.value = false;
      }
    },
  };
  watch(sort, val => {
    // 上报排序信息
    if (val) {
      reporter.report('sort', {
        sort_space: 'space_recycle_bin',
        sortInfo: val,
      });
    }
  });

  ipc.on(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, methods.filterCanOptions);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.COMMAND_FILTER, methods.filterCanOptions);
  });
  return {
    tableData,
    methods,
    loading,
    loadingOnHeader,
    columns,
    sort,
    loadingStatus,
    totalNum,
  };
}
